import {
  FILE_NAMESPACE,
  NORMALIZATION_ALIQUOTS_NAMESPACE_ACTION,
  PLATE_POOLING_NAMESPACE_ACTION,
  PLATE_SIZE_DETAILS_NAMESPACE_ACTION,
  STEPS_NAMESPACE_ACTION,
  PLATE_SIZE_DETAILS_NAMESPACE,
  NORMALIZATION_ALIQUOTS_NAMESPACE,
  PLATE_POOLING_NAMESPACE,
  REMAINDERS_NAMESPACE_ACTION,
  STEPS,
  AMPLISEQ
} from 'src/constants/forms';
import {
  createResource,
  remove,
  set,
  deleteResource,
  addOneNotification,
  setShowLoader,
  setHideLoader,
} from 'src/core/redux/actions';
import { PlateTemplate, DnaOligo, Plate } from 'src/models';
import { state as stateConstants } from 'src/constants';
import { selectStateData } from 'src/core/redux/selectors';
import { getErrorMessagesFromResponse } from 'src/utils/api';
import { addDetails } from 'src/actions';

import { selectFileData } from './selectors';
import messages from './messages';

const getErrorMessagesFromFileRequest = async (response) => {
  let errorMessages = [];

  if (response.status !== 200) {
    const responseData = await response.json();

    errorMessages = await getErrorMessagesFromResponse(responseData);
  }

  return errorMessages;
};

export const removeFileData = () => async (dispatch) => {
  // removes file data
  await dispatch(remove(PlateTemplate.NAME, stateConstants.IDENTIFIER_TYPE_ITEM));

  // resets dna steps
  await dispatch(set(STEPS_NAMESPACE_ACTION, '', DnaOligo.getDefaultStepsState()));

  // clears steps 2,3,4 data
  await dispatch(remove(PLATE_SIZE_DETAILS_NAMESPACE_ACTION, ''));
  await dispatch(remove(NORMALIZATION_ALIQUOTS_NAMESPACE_ACTION, ''));
  await dispatch(remove(PLATE_POOLING_NAMESPACE_ACTION, ''));
  await dispatch(remove(REMAINDERS_NAMESPACE_ACTION, ''));
};

export const updateReduxFileData = (data) => async (dispatch, getState) => {
  const templateData = selectStateData(
    PlateTemplate.NAME,
    stateConstants.IDENTIFIER_TYPE_ITEM
  )(getState()) || {};

  dispatch(set(
    PlateTemplate.NAME,
    stateConstants.IDENTIFIER_TYPE_ITEM,
    {
      ...templateData,
      data: {
        ...(templateData.data || {}),
        ...data,
      }
    }
  ));
};

export const uploadFile = (payload) => async (dispatch) => {
  const formData = new FormData();
  let ampliseq = null;
  const dnaType = DnaOligo.getDNAType();

  if (dnaType) {
    ampliseq = DnaOligo.getAmpliseqType(dnaType);
  }

  formData.append(FILE_NAMESPACE, payload);
  formData.append(AMPLISEQ, ampliseq);

  await dispatch(removeFileData());

  const response = await dispatch(createResource(PlateTemplate)(formData));
  let errorMessages = await getErrorMessagesFromFileRequest(response) || [];

  if (response.status === 200 && !response.plateStatuses?.length) {
    // placeholder for invalid api file. This is a scenario where an invalid file returns
    // no plates instead of error. After the fix this can be removed
    errorMessages = [...errorMessages, messages.dnaTubeInvalidTubeFileEmptyExtended];
  }

  const { plateSize, plateSettingsResponse = {} } = response;

  await dispatch(set(
    PLATE_SIZE_DETAILS_NAMESPACE_ACTION,
    '',
    DnaOligo.getDefaultFormState(
      STEPS.PLATE_SIZE_DETAILS, { plateSize, ...plateSettingsResponse }
    )
  ));

  if (plateSettingsResponse) {
    await dispatch(set(
      NORMALIZATION_ALIQUOTS_NAMESPACE_ACTION,
      '',
      DnaOligo.getDefaultFormState(STEPS.NORMALIZATION_ALIQUOTS)
    ));
    await dispatch(set(
      PLATE_POOLING_NAMESPACE_ACTION,
      '',
      DnaOligo.getDefaultFormState(STEPS.PLATE_POOLING)
    ));
  }

  await dispatch(updateReduxFileData({ size: payload.size, errorMessages }));

  return response;
};

export const resetFileData = set(
  PlateTemplate.NAME,
  stateConstants.IDENTIFIER_TYPE_ITEM,
  {}
);

export const removePlate = (plate) => async (dispatch) => {
  const response = await dispatch(deleteResource(Plate)(plate));

  if (response.status === 200) {
    const {
      basesCount,
      oligosCount,
      plateSize,
      plateStatuses,
      platesCount,
      shippingDays,
    } = response;
    const errorMessages = getErrorMessagesFromFileRequest(response);

    // if last plate was removed, remove file and reset state, otherwise update file data
    const successAction = platesCount === 0 ? removeFileData() : updateReduxFileData({
      basesCount,
      oligosCount,
      plateSize,
      plateStatuses,
      platesCount,
      shippingDays,
      errorMessages,
    });

    dispatch(successAction);

    const platePoolingFormData = addDetails(PLATE_POOLING_NAMESPACE_ACTION);

    dispatch(platePoolingFormData(DnaOligo.getDefaultFormState(STEPS.PLATE_POOLING)));

    await dispatch(addOneNotification({
      type: 'success',
      message: {
        ...(platesCount === 0 ? messages.lastPlateRemoveSuccess : messages.plateRemoveSuccess),
        values: { name: plate.plateName },
      },
    }));

    return;
  }

  await dispatch(addOneNotification({
    type: 'error',
    message: {
      ...messages.plateRemoveError,
      values: { name: plate.plateName },
    },
  }));
};

export const refreshPlates = (forceRefresh = false) => async (dispatch, getState) => {
  // triggers POST request to calculate summary
  // show loader over whole page because all data is dependent of this data
  dispatch(setShowLoader());

  const state = getState();
  const fileData = selectFileData(state);
  const plateSizeData = selectStateData(
    PLATE_SIZE_DETAILS_NAMESPACE
  )(state);
  const normalizationAliquotsData = selectStateData(
    NORMALIZATION_ALIQUOTS_NAMESPACE
  )(state);
  const platePoolingData = selectStateData(PLATE_POOLING_NAMESPACE)(state);

  if (!Object.keys(fileData || {}).length && !forceRefresh) {
    // means the user refreshed the page or landed on an empty page
    dispatch(setHideLoader());

    return;
  }

  const apiData = DnaOligo.processSummaryApiData({
    [PLATE_SIZE_DETAILS_NAMESPACE]: plateSizeData || {},
    [NORMALIZATION_ALIQUOTS_NAMESPACE]: normalizationAliquotsData || {},
    [PLATE_POOLING_NAMESPACE]: platePoolingData || {},
  });

  const response = await dispatch(createResource(Plate)(apiData));

  if (response.status !== 200) {
    await dispatch(addOneNotification({
      type: 'error',
      message: messages.platesSummaryRefreshError,
    }));

    dispatch(setHideLoader());

    return;
  }

  const {
    basesCount,
    oligosCount,
    plateSize,
    plateStatuses,
    platesCount,
    shippingDays,
  } = response;

  await dispatch(updateReduxFileData({
    basesCount,
    oligosCount,
    plateSize,
    plateStatuses,
    platesCount,
    shippingDays,
  }));

  dispatch(setHideLoader());
};
